import React, { useMemo } from 'react'
import queryString from 'query-string'
import { useNavigate, useParams } from 'react-router-dom'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { DocumentObj } from 'src/adapter/AttachmentAdapter'
import { useDocumentsByIds } from 'src/components/async/document/useDocument'
import {
  Grid,
  GridColumn,
  GridHeader,
  GridRow,
} from 'src/components/Reusable/Grid'
import { NoResults } from 'src/components/PageLayout/NoResults'
import { NoPoliciesIcon } from 'src/components/Icons/EmptyStateIcons'
import { TableListLoader } from 'src/components/Reusable/ContentLoaders/TableListLoader'
import { DATE_FORMATS } from 'src/Utils/dateConstants'
import { formatTimestampObject } from 'src/Utils/formatDate'
import { generatePermalink } from 'src/components/PageLayout/Permalink'
import { useHasLinker, useReceptor } from 'src/pages/Receptors/receptors.hooks'
import { LINKER_MODEL_IDS } from 'src/lib/Linkers/Linkers.constants'
import { getEvidenceMimeType } from 'src/pages/Evidence/evidence.helpers'
import { useFileTypeIconForEvidence } from 'src/components/ModalForms/FileType/useFileTypeIcon'
import { BrokenLinkIcon } from 'src/components/Icons/Basic/BrokenLinkIcon'
import { DocumentRow } from 'src/pages/Policies/PoliciesShowPage/DocumentGrid/DocumentItem/DocumentItem.styles'
import { DocumentSource } from 'src/pages/Policies/PoliciesShowPage/DocumentGrid/DocumentItem/DocumentItem.components'

const GRID_TEMPLATE_COLUMNS =
  'min-content minmax(14rem, 1fr) minmax(14rem, 1fr) max-content repeat(4, max-content)'

export const PolicyDocumentsTab = ({
  documentIds,
  policyNamesByModelId,
  emptyStateTitle,
  emptyStateNote,
}: {
  documentIds: string[]
  policyNamesByModelId: Record<string, string>
  emptyStateTitle: string
  emptyStateNote: string
}): JSX.Element => {
  const { documents, isLoading } = useDocumentsByIds({
    documentIds,
    shouldFetch: documentIds.length > 0,
  })
  const sortedDocuments = useMemo(
    () => [...documents].sort((a, b) => b.createdat - a.createdat),
    [documents],
  )

  if (isLoading) {
    return <TableListLoader rows={10} />
  }

  if (sortedDocuments.length === 0) {
    return (
      <NoResults
        icons={<NoPoliciesIcon />}
        title={emptyStateTitle}
        note={emptyStateNote}
      />
    )
  }

  return (
    <Grid style={{ width: '100%' }} gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
      <GridRow>
        <GridHeader>Type</GridHeader>
        <GridHeader>Policy</GridHeader>
        <GridHeader>Document</GridHeader>
        <GridHeader>Source</GridHeader>
        <GridHeader>Last Modified</GridHeader>
        <GridHeader>Last Checked</GridHeader>
        <GridHeader>Last Reviewed</GridHeader>
        <GridHeader>Last Approved</GridHeader>
      </GridRow>
      {sortedDocuments.map((document) => (
        <PolicyDocumentRow
          key={document.newInstanceId || document.id}
          document={document}
          policyNamesByModelId={policyNamesByModelId}
        />
      ))}
    </Grid>
  )
}

const PolicyDocumentRow = ({
  document,
  policyNamesByModelId,
}: {
  document: DocumentObj
  policyNamesByModelId: Record<string, string>
}): JSX.Element => {
  const { pageContext } = useParams()
  const navigate = useNavigate()
  const isLinkedDocument = document.linkerModelId.length > 0
  const { data, isLoading } = useHasLinker(
    document.linkerModelId as LINKER_MODEL_IDS,
    isLinkedDocument,
  )
  const hasLinker = useMemo(() => data?.getHasLinker() || false, [data])
  const { data: linker } = useReceptor({
    modelId: document.linkerModelId as LINKER_MODEL_IDS,
    shouldFetch: hasLinker,
  })
  const invalidCreds = linker?.getIscredvalid() === false
  const exceptions = linker?.getExceptions() || ''
  const actualMime = getEvidenceMimeType(document.mime)
  const Icon = useFileTypeIconForEvidence(false, actualMime, isLinkedDocument)
  const policyModelId = document.subjectmodelid
  const policyName =
    policyNamesByModelId[policyModelId] || policyModelId || 'Unknown policy'
  const policyLink = policyModelId
    ? queryString.stringifyUrl({
        url: generatePermalink({
          pageContext: pageContext as string,
          modelId: policyModelId,
          isInternalLink: true,
          includeSearchParams: false,
          modelType: MODEL_TYPE.POLICY,
        }),
        query: {
          document_id: document.id,
        },
      })
    : ''

  const lastModified = document.relevantdate
    ? formatTimestampObject(document.relevantdate, DATE_FORMATS.ISO_WITH_TIME)
    : ''
  const lastChecked = document.docScannedAt
    ? formatTimestampObject(document.docScannedAt, DATE_FORMATS.ISO_WITH_TIME)
    : ''
  const lastReviewed = document.lastReviewedDate
    ? formatTimestampObject(document.lastReviewedDate, DATE_FORMATS.ISO, true)
    : ''
  const lastApproved = document.lastApprovedDate
    ? formatTimestampObject(document.lastApprovedDate, DATE_FORMATS.ISO, true)
    : ''

  return (
    <DocumentRow
      onClick={() => {
        if (policyLink) {
          navigate(policyLink)
        }
      }}
    >
      <GridColumn>
        {isLoading ? (
          <TableListLoader rows={1} />
        ) : document.isLinkBroken ? (
          <BrokenLinkIcon />
        ) : (
          <Icon width="24px" height="24px" />
        )}
      </GridColumn>
      <GridColumn>{policyName}</GridColumn>
      <GridColumn>{document.caption}</GridColumn>
      <GridColumn>
        <DocumentSource
          document={document}
          hasLinker={hasLinker}
          hasInvalidCreds={invalidCreds}
          exceptions={exceptions}
        />
      </GridColumn>
      <GridColumn>{lastModified}</GridColumn>
      <GridColumn>{lastChecked}</GridColumn>
      <GridColumn>{lastReviewed}</GridColumn>
      <GridColumn>{lastApproved}</GridColumn>
    </DocumentRow>
  )
}
